import { useMemo, useRef, useState } from "react";
import { ColDef, ColumnState } from "ag-grid-community";
import { AgGridReact } from "ag-grid-react";
import { GridContextWrapper } from "src/components/common/gridWrapper/GridContextWrapper";
import Grid_Select from "src/components/common/gridWrapper/Grid_Select";
import { PDA1131_R01ItemDto } from "./PDA1131_R01Dto";
import PDA1131_R01Store from "./PDA1131_R01Store";
import { aggridHeaderClass, pdaCellStyle } from "src/components/common/common";

/** 출고 목록 그리드 행 — 서버 검수량 + 검수 대기 누적 반영 */
type OutboundRow = PDA1131_R01ItemDto & {
  currentInspQty: number;  // inspQty + 검수 대기 shipQty
  remainQty: number;       // orderQty - currentInspQty
  inserted?: boolean;      // GridContextWrapper 호환
};

/** 출하지시 상세 출고 목록 AG Grid */
const PDA1131_R01DetailTable = () => {
  const { shippingDetail, inspectedItems } = PDA1131_R01Store();
  /** AG Grid ref */
  const gridRef = useRef<AgGridReact<OutboundRow>>(null);
  /** GridContextWrapper 래퍼 ref */
  const gridWrapperRef = useRef<HTMLDivElement>(null);
  /** 행 hover 인덱스 (Grid_Select 필수 prop) */
  const [, setHoveredRowIndex] = useState<number | null>(null);
  /** 컬럼 상태 (localStorage 연동) */
  const [columnState, setColumnState] = useState<ColumnState[] | null>(null);

  /** 출하지시 품목 목록 */
  const itemList = shippingDetail?.itemList ?? [];

  /** 검수 대기 수량을 합산한 그리드 데이터 */
  const paddedGridData = useMemo<OutboundRow[]>(() => {
    return itemList.map((item) => {
      const pendingQty = inspectedItems
        .filter((i) => i.itemCd === item.itemCd)
        .reduce((sum, i) => sum + i.shipQty, 0);
      const currentInspQty = item.inspQty + pendingQty;
      const remainQty = Math.max(0, item.orderQty - currentInspQty);
      return { ...item, currentInspQty, remainQty };
    });
  }, [itemList, inspectedItems]);

  /** 상세 출고 목록 컬럼 정의 */
  const columnDefs: ColDef<OutboundRow>[] = useMemo(
    () => [
      {
        headerName: "품명",
        field: "itemNm",
        valueGetter: (params) => params.data?.itemNm ?? "-",
        cellStyle: () => ({ textAlign: "left", ...pdaCellStyle }),
        headerClass: aggridHeaderClass,
        flex: 2,
        minWidth: 80,
      },
      {
        headerName: "지시량",
        field: "orderQty",
        valueGetter: (params) =>
          params.data?.orderQty != null ? params.data.orderQty.toLocaleString() : "-",
        cellStyle: () => ({ textAlign: "right", justifyContent: "flex-end", ...pdaCellStyle }),
        headerClass: aggridHeaderClass,
        flex: 0.9,
        minWidth: 80,
      },
      {
        headerName: "검수량",
        field: "currentInspQty",
        valueGetter: (params) =>
          params.data?.currentInspQty != null
            ? params.data.currentInspQty.toLocaleString()
            : "-",
        cellStyle: (params) => ({
          textAlign: "right",
          justifyContent: "flex-end",
          fontWeight: params.data?.currentInspQty ? "bold" : "normal",
          color: params.data?.currentInspQty ? "#f57c00" : "#999",
          backgroundColor: params.data?.currentInspQty ? "#fff9c4" : "white",
          ...pdaCellStyle,
        }),
        headerClass: aggridHeaderClass,
        flex: 0.9,
        minWidth: 80,
      },
      {
        headerName: "잔량",
        field: "remainQty",
        valueGetter: (params) =>
          params.data?.remainQty != null ? params.data.remainQty.toLocaleString() : "-",
        cellStyle: () => ({ textAlign: "right", justifyContent: "flex-end", ...pdaCellStyle }),
        headerClass: aggridHeaderClass,
        flex: 0.9,
        minWidth: 80,
      },
      {
        headerName: "단위",
        field: "unit",
        valueGetter: (params) => params.data?.unit ?? "-",
        cellStyle: () => ({ textAlign: "center", ...pdaCellStyle }),
        headerClass: aggridHeaderClass,
        flex: 0.5,
        minWidth: 80,
      },
    ],
    []
  );

  /** GridContextWrapper 초기 선택 콜백 (미사용) */
  const initSelected = () => {};

  return (
    <div className="flex-1 p-3 pt-0 flex w-full h-full flex-col overflow-hidden">
      {!shippingDetail ? (
        <div className="flex-1 flex items-center justify-center">
          <div className="text-center text-gray-500">
            <p className="text-lg mb-2">📋</p>
            <p>출하지시를 선택하면</p>
            <p>출고 목록이 표시됩니다</p>
          </div>
        </div>
      ) : itemList.length === 0 ? (
        <div className="flex-1 flex items-center justify-center">
          <div className="text-center text-gray-500">
            <p className="text-lg mb-2">📋</p>
            <p>출고할 품목이 없습니다</p>
          </div>
        </div>
      ) : (
        <GridContextWrapper
          gridRef={gridRef}
          gridWrapperRef={gridWrapperRef}
          paddedGridData={paddedGridData}
          initSelected={initSelected}
          baseColumnDefs={columnDefs}
          setColumnState={setColumnState}
          localStorageKey="PDA1131_R01_OutboundTable_ColumnState"
          enableDataHistory={false}
        >
          <div className="flex-1 w-full h-full min-h-0">
            <Grid_Select<OutboundRow>
              ref={gridRef}
              rowData={paddedGridData}
              columnDefs={columnDefs}
              setHoveredRowIndex={setHoveredRowIndex}
              columnStateKey="PDA1131_R01_OutboundTable_ColumnState"
              setColumnState={setColumnState}
            />
          </div>
        </GridContextWrapper>
      )}
      {shippingDetail && itemList.length > 0 && (
        <div className="px-2 py-2 border-t border-gray-200 bg-white">
          <p className="text-sm text-gray-500 text-center">
            출고 목록 · 품목 스캔 후 검수하면 검수량이 합산 반영됩니다
          </p>
        </div>
      )}
    </div>
  );
};

export default PDA1131_R01DetailTable;
